import { create } from 'zustand';
import api from '../lib/api';
import type {
  PersonListItem,
  PersonDetail,
  PersonFilters,
  PersonCreate,
  PersonUpdate,
  PersonCounts,
  PaginationMeta,
  PeopleSegmentCounts,
  MergedIntoInfo,
} from '../types';

interface PersonState {
  // List state
  people: PersonListItem[];
  pagination: PaginationMeta | null;
  filters: PersonFilters;
  currentPage: number;
  pageSize: number;
  isLoading: boolean;
  error: string | null;

  // Segment state
  counts: PersonCounts | null;
  segment: string | null;

  // Segment counts state (from /api/people/segment-counts)
  segmentCounts: PeopleSegmentCounts | null;
  isLoadingCounts: boolean;

  // Detail state
  currentPerson: PersonDetail | null;
  mergedInto: MergedIntoInfo | null;
  isLoadingDetail: boolean;
  detailError: string | null;

  // Create/Edit state
  isSaving: boolean;
  saveError: string | null;

  // Actions - List
  fetchPeople: () => Promise<void>;
  fetchCounts: () => Promise<void>;
  fetchSegmentCounts: () => Promise<void>;
  setSegment: (segment: string | null) => void;
  setFilters: (filters: Partial<PersonFilters>) => void;
  clearFilters: () => void;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;

  // Actions - Detail
  fetchPerson: (id: string) => Promise<void>;
  clearCurrentPerson: () => void;

  // Actions - CRUD
  createPerson: (data: PersonCreate) => Promise<PersonDetail>;
  updatePerson: (id: string, data: PersonUpdate) => Promise<PersonDetail>;
  deletePerson: (id: string) => Promise<void>;
  bulkAssign: (personIds: string[], assignedUserId: string) => Promise<{ updated: number }>;

  // Actions - Utility
  clearError: () => void;
  clearSaveError: () => void;
}

const DEFAULT_FILTERS: PersonFilters = {
  sortBy: 'name',
  sortOrder: 'asc',
  isActive: true,
  // CRM-16: list opens on "Mine" scope by default (direct + derived-NamedAccount
  // ownership, resolved server-side via peopleWhereMine). Reps see their own
  // people first; one click to "All". clearFilters() resets here, so clearing
  // stays in Mine too.
  assignedUserId: 'me',
};

export const usePersonStore = create<PersonState>((set, get) => ({
  // Initial state - List
  people: [],
  pagination: null,
  filters: DEFAULT_FILTERS,
  currentPage: 1,
  pageSize: 25,
  isLoading: false,
  error: null,

  // Initial state - Segment
  counts: null,
  segment: null,

  // Initial state - Segment counts
  segmentCounts: null,
  isLoadingCounts: false,

  // Initial state - Detail
  currentPerson: null,
  mergedInto: null,
  isLoadingDetail: false,
  detailError: null,

  // Initial state - Create/Edit
  isSaving: false,
  saveError: null,

  // ==================== LIST ACTIONS ====================

  /**
   * Fetch people with current filters and pagination
   */
  fetchPeople: async () => {
    const { filters, currentPage, pageSize } = get();

    try {
      set({ isLoading: true, error: null });

      // Build query params
      const params = new URLSearchParams();
      params.append('page', currentPage.toString());
      params.append('limit', pageSize.toString());

      if (filters.search) params.append('search', filters.search);
      if (filters.stage) params.append('stage', filters.stage);
      if (filters.temperature) params.append('temperature', filters.temperature);
      if (filters.assignedUserId) params.append('assignedUserId', filters.assignedUserId);
      if (filters.organizationId) params.append('organizationId', filters.organizationId);
      if (filters.source) params.append('source', filters.source);
      if (filters.inBlast !== undefined) params.append('inBlast', filters.inBlast.toString());
      if (filters.isActive !== undefined) params.append('isActive', filters.isActive.toString());
      if (filters.segment) params.append('segment', filters.segment);
      if (filters.customerStatus && filters.customerStatus.length > 0) {
        params.append('customerStatus', filters.customerStatus.join(','));
      }
      if (filters.sport) params.append('sport', filters.sport);
      if (filters.role) params.append('role', filters.role);
      if (filters.state && filters.state.length > 0) {
        filters.state.forEach((s) => params.append('state', s));
      }
      if (filters.sortBy) params.append('sortBy', filters.sortBy);
      if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);

      const response = await api.get(`/api/people?${params.toString()}`);

      set({
        people: response.data.people,
        pagination: response.data.pagination,
        isLoading: false,
      });
    } catch (error: any) {
      console.error('Failed to fetch people:', error);
      set({
        isLoading: false,
        error: error.response?.data?.message || 'Failed to load people',
      });
    }
  },

  /**
   * Fetch segment counts for pills
   */
  fetchCounts: async () => {
    try {
      const response = await api.get('/api/people/counts');
      set({ counts: response.data });
    } catch (error) {
      console.error('Failed to fetch people counts:', error);
    }
  },

  /**
   * Fetch in-scope segment counts (from /api/people/segment-counts).
   * Scoped to the current assignedUserId filter.
   */
  fetchSegmentCounts: async () => {
    const { filters } = get();
    set({ isLoadingCounts: true });
    try {
      const params = new URLSearchParams();
      if (filters.assignedUserId) params.append('assignedUserId', filters.assignedUserId);

      const res = await api.get<PeopleSegmentCounts>(
        `/api/people/segment-counts?${params.toString()}`,
      );
      set({ segmentCounts: res.data, isLoadingCounts: false });
    } catch (error: unknown) {
      set({ isLoadingCounts: false });
      console.error('Failed to fetch segment counts:', error);
    }
  },

  /**
   * Set active segment and apply as filter
   */
  setSegment: (segment) => {
    set({ segment });
    get().setFilters({ segment: segment || undefined });
  },

  /**
   * Update filters and reset to page 1.
   * When assignedUserId (scope) changes, also re-fetch segment counts.
   */
  setFilters: (newFilters: Partial<PersonFilters>) => {
    const prevAssigned = get().filters.assignedUserId;
    set((state) => ({
      filters: { ...state.filters, ...newFilters },
      currentPage: 1, // Reset to first page when filters change
    }));
    // Auto-fetch with new filters
    get().fetchPeople();
    // Re-fetch segment counts only when the scope (assignedUserId) actually changed
    if (
      'assignedUserId' in newFilters &&
      newFilters.assignedUserId !== prevAssigned
    ) {
      get().fetchSegmentCounts();
    }
  },

  /**
   * Clear all filters
   */
  clearFilters: () => {
    set({
      filters: DEFAULT_FILTERS,
      currentPage: 1,
    });
    get().fetchPeople();
  },

  /**
   * Set current page
   */
  setPage: (page: number) => {
    set({ currentPage: page });
    get().fetchPeople();
  },

  /**
   * Set page size
   */
  setPageSize: (size: number) => {
    set({ pageSize: size, currentPage: 1 });
    get().fetchPeople();
  },

  // ==================== DETAIL ACTIONS ====================

  /**
   * Fetch single person by ID
   */
  fetchPerson: async (id: string) => {
    try {
      set({ isLoadingDetail: true, detailError: null, mergedInto: null });

      const response = await api.get(`/api/people/${id}`);

      set({
        currentPerson: response.data.person,
        mergedInto: response.data.mergedInto ?? null,
        isLoadingDetail: false,
      });
    } catch (error: any) {
      console.error('Failed to fetch person:', error);
      set({
        isLoadingDetail: false,
        detailError: error.response?.data?.message || 'Failed to load person',
      });
    }
  },

  /**
   * Clear current person (when navigating away)
   */
  clearCurrentPerson: () => {
    // mergedInto must go too — a leftover tombstone pointer would redirect the next person
    // page to the previous person's survivor before its own fetch resolves.
    set({ currentPerson: null, detailError: null, mergedInto: null });
  },

  // ==================== CRUD ACTIONS ====================

  /**
   * Create a new person
   */
  createPerson: async (data: PersonCreate): Promise<PersonDetail> => {
    try {
      set({ isSaving: true, saveError: null });

      const response = await api.post('/api/people', data);

      set({ isSaving: false });

      // Refresh list to include new person
      get().fetchPeople();

      return response.data.person;
    } catch (error: any) {
      console.error('Failed to create person:', error);
      const message = error.response?.data?.message || 'Failed to create person';
      set({ isSaving: false, saveError: message });
      throw new Error(message);
    }
  },

  /**
   * Update an existing person
   */
  updatePerson: async (id: string, data: PersonUpdate): Promise<PersonDetail> => {
    try {
      set({ isSaving: true, saveError: null });

      const response = await api.patch(`/api/people/${id}`, data);

      // Update current person if it's the same one
      const { currentPerson } = get();
      if (currentPerson?.id === id) {
        // mineSource/mineDerivedFrom/isUnclaimed are viewer-derived, not
        // persisted fields — when a response omits one (undefined), keep the
        // loaded value rather than blanking it. A response that includes the
        // field (even as null) wins.
        const nextPerson = { ...response.data.person };
        if (nextPerson.mineSource === undefined) {
          nextPerson.mineSource = currentPerson.mineSource;
        }
        if (nextPerson.mineDerivedFrom === undefined) {
          nextPerson.mineDerivedFrom = currentPerson.mineDerivedFrom;
        }
        if (nextPerson.isUnclaimed === undefined) {
          nextPerson.isUnclaimed = currentPerson.isUnclaimed;
        }
        set({
          currentPerson: nextPerson,
        });
      }

      set({ isSaving: false });

      // Refresh list to reflect changes
      get().fetchPeople();

      return response.data.person;
    } catch (error: any) {
      console.error('Failed to update person:', error);
      const message = error.response?.data?.message || 'Failed to update person';
      set({ isSaving: false, saveError: message });
      throw new Error(message);
    }
  },

  /**
   * Bulk assign people to a user
   */
  bulkAssign: async (personIds: string[], assignedUserId: string): Promise<{ updated: number }> => {
    try {
      set({ isSaving: true, saveError: null });

      const response = await api.patch('/api/people/bulk-assign', {
        personIds,
        assignedUserId,
      });

      set({ isSaving: false });

      // Refresh list to reflect changes
      get().fetchPeople();

      return response.data;
    } catch (error: any) {
      console.error('Failed to bulk assign people:', error);
      const message = error.response?.data?.message || 'Failed to bulk assign people';
      set({ isSaving: false, saveError: message });
      throw new Error(message);
    }
  },

  /**
   * Delete (soft delete) a person
   */
  deletePerson: async (id: string): Promise<void> => {
    try {
      set({ isSaving: true, saveError: null });

      await api.delete(`/api/people/${id}`);

      set({ isSaving: false });

      // Clear current person if it was deleted
      const { currentPerson } = get();
      if (currentPerson?.id === id) {
        set({ currentPerson: null });
      }

      // Refresh list
      get().fetchPeople();
    } catch (error: any) {
      console.error('Failed to delete person:', error);
      const message = error.response?.data?.message || 'Failed to delete person';
      set({ isSaving: false, saveError: message });
      throw new Error(message);
    }
  },

  // ==================== UTILITY ACTIONS ====================

  /**
   * Clear error state
   */
  clearError: () => {
    set({ error: null, detailError: null });
  },

  /**
   * Clear save error state
   */
  clearSaveError: () => {
    set({ saveError: null });
  },
}));
